// deps
import { useCallback, useContext, useEffect, useRef } from "react";

// internal deps
import { ApiResponse } from "@swiggy-private/core-utils";
import { LOADER_TYPE } from "@payment-client/constants/Loader";

// contexts
import { PaymentLoaderContext } from "@payment-client/contexts/PaymentLoaderContext";

// apis
import {
    updateBillImagesPublic,
    UpdateBillImagesParams,
    UpdateBillImagesResponse,
} from "@payment-client/apis/BillImages";

// constants
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";
import { BILL_IMAGES_ERROR_MESSAGES } from "@payment-client/constants/BillImages";

interface UseBillImagesReturn {
    updateBillImages: (params: UpdateBillImagesParams) => Promise<ApiResponse<UpdateBillImagesResponse> | undefined>;
    clearBillImages: () => Promise<ApiResponse<UpdateBillImagesResponse> | undefined>;
}

/**
 * Hook that provides bill images API functionality with loading state management
 * @returns Functions to update and clear bill images with loading state
 */
export const useBillImages = (): UseBillImagesReturn => {
    const { setLoaderState } = useContext(PaymentLoaderContext);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const updateBillImages = useCallback(
        async (params: UpdateBillImagesParams): Promise<ApiResponse<UpdateBillImagesResponse> | undefined> => {
            if (!isMountedRef.current) {
                return;
            }

            const { clearImages, uploadedFileURLs } = params;

            // Validate that only one operation is specified
            if (clearImages && uploadedFileURLs && uploadedFileURLs.length > 0) {
                throw new Error(BILL_IMAGES_ERROR_MESSAGES.CANNOT_CLEAR_AND_UPLOAD_SIMULTANEOUSLY);
            }

            if (!clearImages && !uploadedFileURLs) {
                throw new Error(BILL_IMAGES_ERROR_MESSAGES.MUST_SPECIFY_OPERATION);
            }

            try {
                // Set loading state
                setLoaderState(true, {
                    type: LOADER_TYPE.MOVING_TRAY,
                });

                // Call the API
                const response = await updateBillImagesPublic(DOWNSTREAM_HOSTS.API_HOST, params);

                setLoaderState(false);
                return response;
            } catch (error) {
                // Clear loading state on error only if component is still mounted
                if (isMountedRef.current) {
                    setLoaderState(false);
                }
                throw error;
            }
        },
        [setLoaderState],
    );

    const clearBillImages = useCallback(async (): Promise<ApiResponse<UpdateBillImagesResponse> | undefined> => {
        return updateBillImages({ clearImages: true });
    }, [updateBillImages]);

    return {
        updateBillImages,
        clearBillImages,
    };
};
